import { storageGet, storageRemove, storageSet } from "@/utils/safe-storage";

/** The visitor left the gate for the classic site once; don't open it on its own again. */
export const CLASSIC_KEY = "device-os-classic";
/** The full lid / start-up animation has played once for this visitor. */
export const BOOTED_KEY = "device-os-booted";
/** The gate already opened this session. */
export const SHOWN_KEY = "device-os-shown";
/** Written by `HomePage` when the visitor leaves for a subpage; present means they're coming back. */
export const HOME_SCROLL_KEY = "homepage-scroll-position";

export type GateContext = {
    /** Tablet or desktop width (above the 770px phone breakpoint). */
    isWide: boolean;
    /** `location.hash` without the leading "#". */
    hash: string;
    returningToScroll: boolean;
    prefersClassic: boolean;
    shownThisSession: boolean;
    isCrawler: boolean;
};

/** Whether the gate opens by itself on this home-page load. */
export function shouldOpenGate(ctx: GateContext): boolean {
    return (
        ctx.isWide &&
        ctx.hash === "" &&
        !ctx.returningToScroll &&
        !ctx.prefersClassic &&
        !ctx.shownThisSession &&
        !ctx.isCrawler
    );
}

const CRAWLER = /bot|crawl|spider|slurp|facebookexternalhit|embedly|lighthouse|preview/i;

export function isCrawlerAgent(userAgent: string): boolean {
    return CRAWLER.test(userAgent);
}

/** Reads the live browser state into a `GateContext`. Call during the first render, before `HomePage` clears its scroll key. */
export function readGateContext(isWide: boolean): GateContext {
    return {
        isWide,
        hash: window.location.hash.replace(/^#/, ""),
        returningToScroll: storageGet("session", HOME_SCROLL_KEY) !== null,
        prefersClassic: storageGet("local", CLASSIC_KEY) === "1",
        shownThisSession: storageGet("session", SHOWN_KEY) === "1",
        isCrawler: isCrawlerAgent(window.navigator.userAgent),
    };
}

export function hasBootedBefore(): boolean {
    return storageGet("local", BOOTED_KEY) === "1";
}

export function markGateShown(): void {
    storageSet("session", SHOWN_KEY, "1");
    storageSet("local", BOOTED_KEY, "1");
}

export function rememberClassic(): void {
    storageSet("local", CLASSIC_KEY, "1");
}

export function forgetClassic(): void {
    storageRemove("local", CLASSIC_KEY);
}
